"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { Mic, MicOff, Loader2, AlertCircle } from "lucide-react";
import type { RichContent } from "@/lib/caonabo/rich-content";

type Status = "idle" | "connecting" | "listening" | "speaking" | "error";

const SESSION_KEY = "caonabo_voice_session_id";

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/**
 * Botón de voz Realtime con Grok via WebSocket directo.
 * Flujo:
 *   1. Click → POST /api/caonabo/voice/token
 *   2. WebSocket wss://api.x.ai/v1/realtime con token
 *   3. Envía session.update con instrucciones + tools
 *   4. Captura mic vía MediaRecorder, envía PCM al WS
 *   5. Recibe audio del WS, lo reproduce
 *   6. Si WS pide tool, proxy a /api/caonabo/voice/tool-call
 */
export type VoiceButtonProps = {
  /** Llamado cuando se transcribe lo que dijo el usuario (final). */
  onUserTranscript?: (text: string) => void;
  /** Llamado en cada delta de la respuesta del bot (streaming). */
  onAssistantDelta?: (delta: string) => void;
  /** Llamado cuando el bot terminó la respuesta (text final completo). */
  onAssistantDone?: (text: string) => void;
  /** Llamado al iniciar/terminar de pensar (cuando el bot procesa o llama tools). */
  onThinking?: (active: boolean, detail?: string) => void;
  /** Llamado al ejecutar una tool — para mostrar timeline + indicador. */
  onToolCall?: (info: { name: string; ok: boolean; durationMs: number }) => void;
  /** Llamado cuando el output de una tool produce rich content (tabla/detail/result/confirm). */
  onRichContent?: (content: RichContent) => void;
};

export default function CaonaboVoiceButton(props: VoiceButtonProps = {}) {
  const {
    onUserTranscript,
    onAssistantDelta,
    onAssistantDone,
    onThinking,
    onToolCall,
    onRichContent,
  } = props;
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [micLevel, setMicLevel] = useState(0);
  const [transcript, setTranscript] = useState<string>("");
  const [response, setResponse] = useState<string>("");
  const wsRef = useRef<WebSocket | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  /** Timer que cierra la sesión a los 5 min para acotar costos de Realtime. */
  const sessionTimeoutRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string>("");

  useEffect(() => {
    sessionIdRef.current = getOrCreateSessionId();
  }, []);

  const cleanup = useCallback(() => {
    try {
      mediaRecorderRef.current?.stop();
    } catch {/* ignore */}
    mediaRecorderRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (sessionTimeoutRef.current != null) {
      window.clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    try {
      wsRef.current?.close();
    } catch {/* ignore */}
    wsRef.current = null;
    try {
      audioCtxRef.current?.close();
    } catch {/* ignore */}
    audioCtxRef.current = null;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    setError(null);
    setStatus("connecting");
    try {
      // 1. Mint token
      const tokenRes = await fetch("/api/caonabo/voice/token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const tokenData = await tokenRes.json();
      if (!tokenData.ok) {
        const reason = tokenData.reason ?? "unknown";
        const friendly: Record<string, string> = {
          chat_voice_disabled: "La voz está desactivada en /admin/configuracion-ai.",
          unauthenticated: "Inicia sesión para usar voz.",
          xai_api_key_missing: "Falta XAI_API_KEY (configúrala en admin).",
          voice_provider_anthropic_no_soportado:
            "Voz solo soporta Grok actualmente. Cambia voiceProvider a grok en admin.",
          voice_provider_none_no_soportado:
            "Voz desactivada. Selecciona voiceProvider=grok en admin.",
        };
        // Si reason trae detalle (decrypt failed, db error), mostrarlo crudo
        if (reason.startsWith("xai_decrypt_failed")) {
          setError(reason);
        } else if (reason.startsWith("db_error")) {
          setError(`BD: ${reason.replace("db_error: ", "")}`);
        } else {
          setError(friendly[reason] ?? `Error: ${reason}`);
        }
        setStatus("error");
        return;
      }

      // 2. Pedir mic
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // 3. WebSocket con token Bearer + subprotocols.
      // x.ai Realtime usa el mismo protocolo OpenAI Realtime. El token
      // client_secret va vía Sec-WebSocket-Protocol porque los browsers
      // no permiten Authorization header en WebSocket. Algunos servers
      // requieren también "openai-beta.realtime-v1".
      const wsUrl = `${tokenData.websocketUrl}?model=${encodeURIComponent(tokenData.model)}`;
      console.log("[caonabo-voice] connecting WS:", wsUrl);
      const ws = new WebSocket(wsUrl, [
        "realtime",
        `openai-insecure-api-key.${tokenData.token}`,
        "openai-beta.realtime-v1",
      ]);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("[caonabo-voice] WS open");
        // Enviar session config inmediato
        ws.send(JSON.stringify(tokenData.sessionConfig));
        setStatus("listening");
        // Cap de costo: cierra sesión a los 15 min para evitar runaway.
        // Override por env NEXT_PUBLIC_CAONABO_VOICE_MAX_MS (default 900000ms = 15min).
        const maxMs =
          Number(
            (typeof process !== "undefined" && process.env.NEXT_PUBLIC_CAONABO_VOICE_MAX_MS) || 0
          ) || 900_000;
        sessionTimeoutRef.current = window.setTimeout(() => {
          console.log("[caonabo-voice] sesión cerrada por timeout de 15 min");
          // Mensaje suave (no error rojo) — comportamiento esperado.
          setError(null);
          cleanup();
          setStatus("idle");
        }, maxMs);
      };

      ws.onmessage = async (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          // Log defensivo de TODOS los eventos para debug
          if (msg.type) console.log(`[caonabo-voice] ${msg.type}`, msg);

          // VAD: detección de inicio/fin de habla
          if (msg.type === "input_audio_buffer.speech_started") {
            setStatus("listening");
            setTranscript("");
            onThinking?.(false);
            // Cuando el usuario empieza a hablar, cortar audio del bot.
            // El server-side ya lo cancela vía interrupt_response, pero
            // hay buffer en cola del cliente que también hay que tirar.
            stopAllAudio();
            // Avisar al servidor para que cancele response en curso.
            try {
              ws.send(JSON.stringify({ type: "response.cancel" }));
            } catch {/* ignore */}
          } else if (msg.type === "input_audio_buffer.speech_stopped") {
            setStatus("speaking"); // procesando
            onThinking?.(true, "Procesando…");
          } else if (msg.type === "input_audio_buffer.committed") {
            // El audio ya se commiteó al servidor, esperando response
            setStatus("speaking");
            onThinking?.(true, "Pensando…");
          }
          // Transcripción del usuario
          else if (
            msg.type === "conversation.item.input_audio_transcription.completed" ||
            msg.type === "conversation.item.input_audio_transcription.delta"
          ) {
            const txt = msg.transcript ?? msg.delta ?? "";
            if (txt) setTranscript((prev) => prev + txt);
            // Cuando es .completed → empujar mensaje user al chat principal.
            if (
              msg.type === "conversation.item.input_audio_transcription.completed" &&
              msg.transcript &&
              onUserTranscript
            ) {
              onUserTranscript(msg.transcript);
            }
          }
          // Respuesta del LLM (texto). x.ai usa "output_audio_transcript",
          // OpenAI clásico usa "audio_transcript". Aceptamos ambos.
          else if (
            (msg.type === "response.audio_transcript.delta" ||
              msg.type === "response.output_audio_transcript.delta") &&
            msg.delta
          ) {
            setResponse((prev) => prev + msg.delta);
            onAssistantDelta?.(msg.delta);
          } else if (
            msg.type === "response.audio_transcript.done" ||
            msg.type === "response.output_audio_transcript.done"
          ) {
            if (msg.transcript) setResponse(msg.transcript);
            if (msg.transcript) onAssistantDone?.(msg.transcript);
          }
          // Audio out — x.ai: response.output_audio.delta, OpenAI: response.audio.delta
          else if (
            (msg.type === "response.audio.delta" ||
              msg.type === "response.output_audio.delta") &&
            msg.delta
          ) {
            await playAudioDelta(msg.delta);
            setStatus("speaking");
          } else if (
            msg.type === "response.done" ||
            msg.type === "response.audio.done" ||
            msg.type === "response.output_audio.done"
          ) {
            setStatus("listening");
            onThinking?.(false);
          } else if (msg.type === "response.function_call_arguments.done") {
            // Tool call: hacer proxy al server
            const toolName = msg.name as string;
            const callId = msg.call_id as string;
            const argsRaw = msg.arguments as string;
            let parsedArgs: Record<string, unknown> = {};
            try {
              parsedArgs = JSON.parse(argsRaw);
            } catch {/* ignore */}
            // Avisar al widget que estamos pensando + ejecutando tool.
            onThinking?.(true, `Ejecutando ${toolName}…`);
            const r = await fetch("/api/caonabo/voice/tool-call", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                sessionId: sessionIdRef.current,
                toolName,
                input: parsedArgs,
              }),
            });
            const data = await r.json();
            // Notificar al widget el tool call (para timeline).
            onToolCall?.({
              name: toolName,
              ok: Boolean(data.ok),
              durationMs: Number(data.durationMs ?? 0),
            });
            // Si el output produjo rich content (tabla, detail, result, confirm),
            // empujarlo al chat para que el usuario lo vea visualmente además del audio.
            if (data.richContent) {
              onRichContent?.(data.richContent);
            }
            onThinking?.(false);
            // Enviar resultado de vuelta al WS
            ws.send(
              JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "function_call_output",
                  call_id: callId,
                  output: JSON.stringify(data.ok ? data.output : { error: data.error }),
                },
              })
            );
            // Pedir respuesta
            ws.send(JSON.stringify({ type: "response.create" }));
          } else if (msg.type === "error") {
            console.error("[caonabo-voice] error event", msg);
            setError(
              `WS error: ${msg.error?.message ?? msg.error?.code ?? JSON.stringify(msg.error ?? msg).slice(0, 200)}`
            );
            setStatus("error");
          }
        } catch (parseErr) {
          console.warn("[caonabo-voice] parse error", parseErr, ev.data);
        }
      };

      ws.onerror = (e) => {
        // onerror no trae detalle. El motivo real llega en onclose.
        console.error("[caonabo-voice] WS onerror", e);
      };

      ws.onclose = (ev) => {
        console.warn("[caonabo-voice] WS closed", {
          code: ev.code,
          reason: ev.reason,
          wasClean: ev.wasClean,
        });
        if (ev.wasClean && ev.code === 1000) {
          setStatus("idle");
          return;
        }
        // 1006 = handshake falló (URL/subprotocolo/token rechazado, no llegó a abrir)
        // 1008 = policy violation (auth rechazada)
        // 4xxx = códigos custom del server
        const detail =
          ev.code === 1006
            ? "handshake falló (URL inválida, token rechazado o subprotocolo no soportado). Revisa /api/caonabo/voice/token y verifica que XAI_API_KEY tenga acceso a Realtime."
            : `${ev.code}${ev.reason ? ` — ${ev.reason}` : " (sin razón)"}`;
        setError(`WebSocket cerrado: ${detail}`);
        setStatus("error");
      };

      // 4. Capturar audio del mic con AudioWorkletNode (no deprecated)
      const audioCtx = new AudioContext({ sampleRate: 24000 });
      audioCtxRef.current = audioCtx;
      try {
        await audioCtx.audioWorklet.addModule("/caonabo-pcm-processor.js");
      } catch (modErr) {
        console.error("[caonabo-voice] addModule failed", modErr);
        throw new Error(
          "No se pudo cargar el procesador de audio. Recarga la página."
        );
      }
      const source = audioCtx.createMediaStreamSource(stream);
      const worklet = new AudioWorkletNode(audioCtx, "caonabo-pcm-processor");
      source.connect(worklet);
      // No conectamos al destination — evita eco. El worklet procesa y
      // envía vía port.postMessage al main thread.
      worklet.port.onmessage = (ev) => {
        const data = ev.data;
        if (data instanceof ArrayBuffer) {
          // Chunk de PCM16 → enviar al WS
          if (ws.readyState !== WebSocket.OPEN) return;
          const b64 = arrayBufferToBase64(data);
          try {
            ws.send(
              JSON.stringify({ type: "input_audio_buffer.append", audio: b64 })
            );
          } catch {
            /* ws closed mid-send */
          }
        } else if (data && typeof data.level === "number") {
          // Nivel RMS para visualización (0..1)
          setMicLevel(data.level);
        }
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error inesperado");
      setStatus("error");
      cleanup();
    }
  }, [status, cleanup]);

  const stop = useCallback(() => {
    cleanup();
    setStatus("idle");
    setTranscript("");
    setResponse("");
  }, [cleanup]);

  const isActive = status === "listening" || status === "speaking" || status === "connecting";
  // Halo dinámico proporcional al nivel del mic (0..1) cuando listening
  const haloPx = status === "listening" ? Math.min(16, Math.round(micLevel * 60)) : 0;
  const haloOpacity = status === "listening" ? Math.min(0.55, micLevel * 1.5) : 0;

  return (
    <div className="inline-flex flex-col items-stretch gap-1">
      <button
        type="button"
        onClick={isActive ? stop : start}
        aria-label={isActive ? "Detener voz" : "Iniciar voz"}
        className="inline-flex items-center justify-center rounded-full p-2 transition-all relative"
        style={{
          background: isActive
            ? "linear-gradient(135deg, #8B3A2E 0%, #6B2820 100%)"
            : "rgba(139,58,46,0.10)",
          border: `1px solid ${isActive ? "transparent" : "rgba(139,58,46,0.30)"}`,
          color: isActive ? "white" : "#D77F70",
          boxShadow:
            status === "listening"
              ? `0 0 0 ${haloPx}px rgba(139,58,46,${haloOpacity}), 0 0 0 4px rgba(139,58,46,0.25)`
              : isActive
                ? "0 0 0 4px rgba(139,58,46,0.25)"
                : undefined,
          width: 36,
          height: 36,
          transition: "box-shadow 80ms linear, background 200ms",
        }}
        title={
          status === "idle"
            ? "Hablar con Caonabo"
            : status === "connecting"
              ? "Conectando…"
              : status === "listening"
                ? "Escuchando…"
                : status === "speaking"
                  ? "Hablando…"
                  : "Reintentar"
        }
      >
        {status === "connecting" ? (
          <Loader2 size={14} className="animate-spin" />
        ) : status === "error" ? (
          <AlertCircle size={14} />
        ) : isActive ? (
          <MicOff size={14} />
        ) : (
          <Mic size={14} />
        )}
      </button>
      {error && (
        <span className="text-[9px] text-red-300 max-w-[220px] break-words">{error}</span>
      )}
      {(transcript || response) && (
        <div
          className="absolute bottom-full mb-2 right-0 w-[260px] rounded-lg p-2 text-[10px] space-y-1 caonabo-fade-in"
          style={{
            background: "rgba(20,20,28,0.95)",
            border: "1px solid rgba(139,58,46,0.30)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          }}
        >
          {transcript && (
            <div>
              <span className="text-text-muted text-[9px]">Tú: </span>
              <span className="text-text">{transcript}</span>
            </div>
          )}
          {response && (
            <div>
              <span className="text-[9px]" style={{ color: "#D77F70" }}>Caonabo: </span>
              <span className="text-text">{response}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function arrayBufferToBase64(buffer: ArrayBuffer | ArrayBufferLike): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

let audioQueueCtx: AudioContext | null = null;
let audioQueueTime = 0;
/** Sources actualmente en cola — se cancelan cuando el usuario habla. */
const activeAudioSources: AudioBufferSourceNode[] = [];

/** Detiene TODO el audio del bot que esté reproduciéndose o en cola. */
export function stopAllAudio(): void {
  for (const src of activeAudioSources) {
    try {
      src.stop();
      src.disconnect();
    } catch {/* ignore */}
  }
  activeAudioSources.length = 0;
  if (audioQueueCtx) {
    audioQueueTime = audioQueueCtx.currentTime;
  }
}

async function playAudioDelta(b64: string): Promise<void> {
  if (typeof window === "undefined") return;
  if (!audioQueueCtx) {
    audioQueueCtx = new AudioContext({ sampleRate: 24000 });
    audioQueueTime = audioQueueCtx.currentTime;
  }
  const ctx = audioQueueCtx;
  // Chrome autoplay policy: el ctx puede estar "suspended" aunque haya
  // habido gesture en el botón mic. Forzar resume.
  if (ctx.state === "suspended") {
    try { await ctx.resume(); } catch {/* ignore */}
  }
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const pcm16 = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  const float32 = new Float32Array(pcm16.length);
  for (let i = 0; i < pcm16.length; i++) float32[i] = pcm16[i] / 0x8000;
  const buffer = ctx.createBuffer(1, float32.length, 24000);
  buffer.getChannelData(0).set(float32);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(ctx.destination);
  const startAt = Math.max(audioQueueTime, ctx.currentTime);
  src.start(startAt);
  audioQueueTime = startAt + buffer.duration;
  activeAudioSources.push(src);
  // Limpiar el source cuando termine
  src.onended = () => {
    const idx = activeAudioSources.indexOf(src);
    if (idx >= 0) activeAudioSources.splice(idx, 1);
  };
}
